import { useState } from "react";
import { Card, Overline } from "@/components/common";
import { cn } from "@/lib/utils";
import { Sparkle, Check, ArrowRight, TriangleAlert, TrendingUp, Wrench, Users, Package } from "lucide-react";

const CAT_META = {
  Critical: { icon: TriangleAlert, color: "#DC2626", tint: "bg-[#DC2626]/[0.04] border-[#DC2626]/20" },
  Operations: { icon: Wrench, color: "#2563EB", tint: "bg-[#2563EB]/[0.04] border-[#2563EB]/15" },
  Revenue: { icon: TrendingUp, color: "#0F3F2D", tint: "bg-[#0F3F2D]/[0.04] border-[#0F3F2D]/15" },
  Customer: { icon: Users, color: "#B08423", tint: "bg-[#C7A24A]/[0.06] border-[#C7A24A]/25" },
  Inventory: { icon: Package, color: "#52525B", tint: "bg-[#F3F4F6] border-[#E5E7EB]" },
};

export default function IntelligenceCard({ rec, onAction, testid, compact }) {
  const [state, setState] = useState(rec.state || "open");
  const [label, setLabel] = useState(rec.state_label);
  const [loading, setLoading] = useState(false);
  const meta = CAT_META[rec.category] || CAT_META.Revenue;
  const Icon = meta.icon;
  const actioned = state === "actioned";

  const handle = async () => {
    setLoading(true);
    const res = await onAction?.(rec);
    setLoading(false);
    setState("actioned");
    setLabel(res?.label || "Actioned");
  };

  return (
    <Card className={cn("p-6 border", meta.tint)} data-testid={testid}>
      <div className="flex items-start gap-4">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border"
          style={{ borderColor: `${meta.color}33`, background: `${meta.color}0d` }}
        >
          <Icon size={17} style={{ color: meta.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Sparkle size={12} className="text-[#C7A24A]" />
            <Overline className="!text-[#71717A]">{rec.title || rec.category}</Overline>
            {rec.severity_label && (
              <span
                className="ml-auto text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border"
                style={{ color: meta.color, borderColor: `${meta.color}33`, background: `${meta.color}0d` }}
              >
                {rec.severity_label}
              </span>
            )}
          </div>
          <p className="text-[15px] font-semibold text-[#1C1F1E] leading-snug">{rec.problem}</p>
          {rec.data && <p className="text-sm text-[#52525B] mt-1.5 leading-relaxed">{rec.data}</p>}

          <div className={cn("mt-4 grid gap-4", compact ? "sm:grid-cols-2" : "sm:grid-cols-2")}>
            <div>
              <Overline>Recommended Action</Overline>
              <p className="text-sm text-[#1C1F1E] mt-1 font-medium">{rec.action}</p>
            </div>
            <div>
              <Overline>Expected Impact</Overline>
              <p className="text-sm font-semibold mt-1" style={{ color: meta.color }}>{rec.impact}</p>
            </div>
          </div>

          <div className="mt-4">
            {actioned ? (
              <span
                data-testid={`${testid}-done`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#059669]/10 text-[#059669] text-sm font-medium border border-[#059669]/20"
              >
                <Check size={15} /> {label}
              </span>
            ) : (
              <button
                data-testid={`${testid}-action`}
                onClick={handle}
                disabled={loading}
                className="group inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0F3F2D] hover:bg-[#165c42] text-white text-sm font-medium transition-colors active:scale-95 disabled:opacity-60"
              >
                {loading ? "Working..." : rec.button}
                {!loading && <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />}
              </button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
